import { useState } from 'react';
import { Identity } from '../types/auth';
import { User, Shield, Code, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../utils/cn';
import { useUsername } from '../services/usernameCache';
import { PlayerProfile } from './PlayerProfile';
import { SectionHeader } from './ui/SectionHeader';
import { SkeletonLoader } from './SkeletonLoader';

// Helper function to generate a color from a string
const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const InfoRow = ({ label, value }: { label: string, value: string }) => (
    <div className="flex items-center justify-between gap-4 py-2 text-sm">
        <span className="text-xs text-glass-400 shrink-0">{label}</span>
        <span className="text-white font-mono text-xs tabular-nums truncate" title={value}>{value}</span>
    </div>
);

const ClickToRevealInfoRow = ({ label, value }: { label: string, value: string }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div className="flex items-center justify-between gap-4 py-2 text-sm">
            <span className="text-xs text-glass-400 shrink-0">{label}</span>
            {isVisible ? (
                <span className="text-white font-mono text-xs tabular-nums truncate" title={value}>{value}</span>
            ) : (
                <button type="button" onClick={() => setIsVisible(true)} className="glass-button-secondary btn-xs">
                    Click to reveal
                </button>
            )}
        </div>
    );
};

/** Neutral badge with a data-derived colour dot (keeps the per-group colour, stays legible in both themes). */
const ColorDotBadge = ({ color, label }: { color: string, label: string }) => (
    <span className="badge badge-neutral">
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
        {label}
    </span>
);

export const UserInfo = ({ identity }: { identity: Identity | null }) => {
    const [isDebugVisible, setIsDebugVisible] = useState(false);

    if (!identity) {
        return (
            <div className="glass-card p-4 md:p-5" aria-busy="true">
                <span className="sr-only">Loading user info...</span>
                <div className="flex items-center gap-3 mb-5">
                    <SkeletonLoader className="h-5 w-8" />
                    <SkeletonLoader className="h-5 w-5 rounded-[3px]" />
                    <SkeletonLoader className="h-5 w-32" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <SkeletonLoader className="h-4 w-20 mb-4" />
                        <div className="space-y-3">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="flex items-center justify-between gap-4">
                                    <SkeletonLoader className="h-3 w-24" />
                                    <SkeletonLoader className="h-3 w-40" />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <SkeletonLoader className="h-4 w-20 mb-4" />
                        <div className="flex flex-wrap gap-1.5">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <SkeletonLoader key={i} className="h-5 w-24 rounded-full" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const minecraftUuid = identity?.raw?.custom?.minecraft_uuid;
    // Use the automatic UUID-to-username conversion if username isn't already in identity
    const cachedMcUsername = useUsername(minecraftUuid || '');
    const mcUsername = identity?.raw?.custom?.username || cachedMcUsername;
    const welcomeName = mcUsername || identity?.name || identity?.raw.id || 'User';

    const spyGroups = identity.raw.custom.groups
        .filter(g => g.startsWith('SPY_'))
        .map(g => g.replace('SPY_', ''));

    return (
        <div className="glass-card p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-5 text-lg font-semibold tracking-tight">
                <h2 className="text-white">Hi,</h2>
                {minecraftUuid ? (
                    <PlayerProfile 
                        uuid={minecraftUuid} 
                        name={mcUsername || undefined}
                        showUuid={false}
                        showAvatar={true}
                        inline={true}
                    />
                ) : (
                    <span className="text-white">{welcomeName}</span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Primary Info */}
                <div className="min-w-0">
                    <SectionHeader title="Identity" icon={User} />
                    <div className="divide-y divide-glass-200/10">
                        {minecraftUuid && (
                            <InfoRow label="Minecraft UUID" value={minecraftUuid} />
                        )}
                        {mcUsername && (
                            <InfoRow label="Minecraft Username" value={mcUsername} />
                        )}
                        <InfoRow label="ID" value={identity.raw.id} />
                        <InfoRow label="Email" value={identity.email} />
                        <ClickToRevealInfoRow label="IP Address" value={identity.raw.ip} />
                        <InfoRow label="Country" value={identity.raw.geo.country} />
                    </div>
                </div>

                {/* Groups */}
                <div className="min-w-0">
                    <SectionHeader title="Site Roles" icon={Shield} count={spyGroups.length > 0 ? spyGroups.length : undefined} />
                    {spyGroups.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {spyGroups.map(group => {
                                const formattedGroup = group.replace(/_/g, ' ');
                                const color = stringToColor(group); // Use original group name for consistent color
                                return (
                                    <ColorDotBadge key={group} color={color} label={formattedGroup} />
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-glass-400">No specific site roles assigned.</p>
                    )}
                </div>
            </div>

            {/* Staff Teams Section */}
            {identity.raw.custom.staff_teams && identity.raw.custom.staff_teams.length > 0 && (
                <div className="mt-5 border-t border-glass-200/10 pt-4">
                    <SectionHeader title="Staff Teams" icon={User} count={identity.raw.custom.staff_teams.length} />
                    <div className="flex flex-wrap gap-1.5">
                        {identity.raw.custom.staff_teams.map(team => {
                            const formattedTeam = team.charAt(0).toUpperCase() + team.slice(1);
                            const color = stringToColor(team);
                            return (
                                <ColorDotBadge key={team} color={color} label={formattedTeam} />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Debug Info */}
            <div className="mt-5 border-t border-glass-200/10 pt-3">
                <button 
                    type="button"
                    onClick={() => setIsDebugVisible(!isDebugVisible)} 
                    className="btn-ghost -ml-2.5"
                    aria-expanded={isDebugVisible}
                >
                    <Code className="w-4 h-4" />
                    <span className="text-sm font-medium">Debug Information</span>
                    <ChevronDown className={cn("w-4 h-4 transition-transform", isDebugVisible && "rotate-180")} />
                </button>
                <AnimatePresence>
                {isDebugVisible && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2 overflow-hidden"
                    >
                        <div className="space-y-3">
                            <div>
                                <h4 className="eyebrow mb-1.5">Permissions ({identity.raw.custom.permissions?.length || 0})</h4>
                                {identity.raw.custom.permissions && identity.raw.custom.permissions.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {identity.raw.custom.permissions.map(perm => (
                                            <span key={perm} className="chip font-mono">
                                                {perm}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-glass-400">No permissions assigned</p>
                                )}
                            </div>
                            <details className="mt-2">
                                <summary className="text-xs font-medium text-glass-400 cursor-pointer hover:text-white select-none">Raw Identity Data</summary>
                                <pre className="subcard text-white p-3 mt-2 overflow-x-auto text-xs font-mono">
                                    <code>
                                        {JSON.stringify(identity, null, 2)}
                                    </code>
                                </pre>
                            </details>
                        </div>
                    </motion.div>
                )}
                </AnimatePresence>
            </div>
        </div>
    );
};
